import { memo, useCallback, useMemo, useRef, useState } from 'react';

import { ErrorBoundary } from '@src/components/ErrorBoundary';
import {
  Box,
  Button,
  ButtonVariants,
  CardViewEnum,
  InternalButtonSpan,
  isMultileg,
  MarketDataCardState,
  MaxOrderSizeDialogs,
  MixpanelEvent,
  MarketDataCard as SharedMarketDataCard,
  Text,
  Tour,
  UnifiedLiquidityEnum,
  useCallbackRef,
  useMixpanel,
  useSecuritiesContext,
  useSecurity,
  useSyncedRef,
  VStack,
  type Security,
} from '@talos/kyoko';
import styled from 'styled-components';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';
import { useTradingSettings } from '../../providers/TradingSettingsContext';
import { COLUMN_WIDTH } from '../../styles/dimensions';
import { TourName } from '../../tokens/tours';
import { useMarketCardsConfig } from '../AppLayout/Panels/Trading/MarketDataCardsPanel/MarketCardsConfigContext';
import { DeepDiveButtonElement } from '../DeepDive/DeepDiveButton';
import { usePanelContext } from '../Layout/providers/PanelContextProvider';
import type { MarketSelectorItem } from '../MarketSelector/types';
import { MarketCardMarketAccountButtonElement } from './MarketCardMarketAccountButton';
import { MarketDataCardHeader } from './MarketDataCardHeader';
import { MarketDataCardSettings } from './MarketDataCardSettings';
import { MarketDataCardSlides } from './MarketDataCardSlides';
import { MarketDataCardQuickMarketSelectorPanel } from './QuickMarketSelector/MarketDataCardQuickMarketSelectorPanel';
import { MarketDataCardQuickMarketSelectorProvider } from './QuickMarketSelector/MarketDataCardQuickMarketSelectorProvider';
import { ClearCustomRateDialog, PublishCustomRateDialog, useCustomRateDialogs } from './components/CustomRateDialog';
import { hideNavigableControlArrowsUntilHover } from './components/navigableControlRowStyles';
import type { MarketDataCardProps } from './types';
import { useHandleClickMarketPrice } from './useHandleClickMarketPrice';
import type { PersistMarketDataCardMarketSelection } from './useMarketDataCardMarketSelector';
import { useMarketDataCardPreparedConfig } from './useMarketDataCardPreparedConfig';

export const MarketDataCard = ({ config, onSave, onDelete, ...props }: MarketDataCardProps) => {
  const { config: preparedConfig, marketSelectorItems } = useMarketDataCardPreparedConfig({
    config,
    onSave,
    onDelete,
  });
  return (
    <InnerMarketDataCard
      config={preparedConfig}
      marketSelectorItems={marketSelectorItems}
      onSave={onSave}
      onDelete={onDelete}
      {...props}
    />
  );
};

type InnerMarketDataCardProps = MarketDataCardProps & {
  marketSelectorItems: MarketSelectorItem[];
};

const InnerMarketDataCard = memo(function MarketDataCard({
  config,
  marketSelectorItems,
  isDraggable,
  isDragged,
  defaultSubAccountId,
  onSave,
  onDelete,
  onHidePlaceholders,
  ...props
}: InnerMarketDataCardProps) {
  const mixpanel = useMixpanel();
  const { forceCompact } = useMarketCardsConfig();
  const { useNewMarketSelector } = useTradingSettings();
  const { enableInlineMultilegMarketEditing } = useFeatureFlag();
  const { isLoaded } = useSecuritiesContext();
  const { isPopout } = usePanelContext();
  const isInMobileMode = useIsMobileLayout();
  const initializedConfig = config;
  const {
    detailsExpanded,
    symbol,
    view: configView,
    state,
    selectedMarketAccounts,
    selectedMarkets,
    unifiedLiquidity,
    selectedAggregation,
    orderPresetID,
    subAccount,
  } = initializedConfig;
  const view = forceCompact || isInMobileMode ? CardViewEnum.COMPACT : configView;
  const [showSettings, setShowSettings] = useState(state === MarketDataCardState.NEW_CARD);
  const [quickPanelOpen, setQuickPanelOpen] = useState(false);
  const [marketSelectorInventoryActive, setMarketSelectorInventoryActive] = useState(false);
  const resolvedSecurity: Security | undefined = useSecurity(symbol);
  const customRateDialogs = useCustomRateDialogs({
    security: resolvedSecurity,
    selectedMarketAccounts,
  });
  const cardRef = useRef<HTMLDivElement>(null);

  const handleToggleSettings = useCallback(() => {
    mixpanel.track(showSettings ? MixpanelEvent.CloseCardSettings : MixpanelEvent.OpenCardSettings);
    setShowSettings(!showSettings);
  }, [mixpanel, showSettings]);

  const showQuickMarketSelector =
    useNewMarketSelector &&
    resolvedSecurity != null &&
    // If not multileg, all good. If multileg, the quick market selector is feature flagged.
    (!isMultileg(resolvedSecurity) || (isMultileg(resolvedSecurity) && enableInlineMultilegMarketEditing));
  const showMultilegQuickMarketSelectorTour = showQuickMarketSelector && isMultileg(resolvedSecurity);

  const handleOpenMarketSelector = useCallback(() => {
    setMarketSelectorInventoryActive(true);
    setQuickPanelOpen(true);
  }, []);

  const handleCloseQuickPanel = useCallback(() => {
    setQuickPanelOpen(false);
  }, []);

  const ulEnabled = unifiedLiquidity === UnifiedLiquidityEnum.Enabled;

  const configRef = useSyncedRef(initializedConfig);

  const onPersistMarketSelection = useCallbackRef((update: PersistMarketDataCardMarketSelection) => {
    onSave({
      ...configRef.current,
      selectedAggregation: update.selectedAggregation,
      selectedMarkets: update.selectedMarkets,
      selectedMarketAccounts: update.selectedMarketAccounts,
      unifiedLiquidityMarketSymbols:
        update.unifiedLiquidityMarketSymbols ?? configRef.current.unifiedLiquidityMarketSymbols,
      unifiedLiquidity: update.unifiedLiquidity ?? configRef.current.unifiedLiquidity,
      availableMarkets: update.selectedMarkets,
      availableMarketAccounts: update.selectedMarketAccounts,
    });
  });

  const {
    clickToTradePreviewOrder,
    onClickPrice: handleClickPrice,
    onCancelActive: handleCancelActive,
    maxOrderSizeDialogs,
  } = useHandleClickMarketPrice({
    symbol,
    unifiedLiquidity,
    subAccount,
    defaultSubAccountId,
    orderPresetID,
    marketAccounts: selectedMarketAccounts,
    unifiedLiquidityMarketSymbols: initializedConfig.unifiedLiquidityMarketSymbols,
    markets: selectedMarkets,
    aggregation: selectedAggregation,
  });

  const handleDeleteCard = useCallback(() => {
    onDelete(initializedConfig);
    if (state === MarketDataCardState.NEW_CARD) {
      onHidePlaceholders();
    }
  }, [initializedConfig, onDelete, onHidePlaceholders, state]);

  const showDelete = useMemo(
    () => !showSettings && resolvedSecurity == null && isLoaded,
    [showSettings, resolvedSecurity, isLoaded]
  );

  const cardContent = useMemo(
    () => (
      <SharedMarketDataCard
        ErrorBoundary={ErrorBoundary}
        isDragged={isDragged}
        isExpanded={!!detailsExpanded}
        view={showSettings ? CardViewEnum.NORMAL : view}
      >
        {showDelete ? (
          <VStack gap="spacing8" h="100%" justifyContent="center" alignItems="center">
            <Text>Cannot find symbol {initializedConfig.symbol}</Text>
            <Button onClick={handleDeleteCard} variant={ButtonVariants.Negative}>
              Delete Card
            </Button>
          </VStack>
        ) : showSettings ? (
          <MarketDataCardSettings
            config={initializedConfig}
            onClose={handleToggleSettings}
            saveCard={onSave}
            deleteCard={onDelete}
            hidePlaceholderCards={onHidePlaceholders}
            cardRef={cardRef}
          />
        ) : (
          <>
            <MarketDataCardHeader
              isDraggable={isDraggable}
              config={initializedConfig}
              security={resolvedSecurity}
              activeOrder={clickToTradePreviewOrder}
              onClickSettings={handleToggleSettings}
              onClickPrice={handleClickPrice}
              onCancelActive={handleCancelActive}
              onSaveCard={onSave}
              onDelete={onDelete}
              defaultSubAccountId={defaultSubAccountId}
              onOpenMarketSelector={showQuickMarketSelector ? handleOpenMarketSelector : undefined}
              marketSelectorTourDataTestId={
                showMultilegQuickMarketSelectorTour
                  ? 'market-card-multileg-quick-market-selector-tour-anchor'
                  : undefined
              }
              onOpenPublishCustomRate={customRateDialogs.onOpenPublishCustomRate}
              onOpenClearCustomRate={customRateDialogs.onOpenClearCustomRate}
              canClearCustomRate={customRateDialogs.canClearCustomRate}
            />
            <PublishCustomRateDialog dialog={customRateDialogs.publishDialogProps} />
            <ClearCustomRateDialog dialog={customRateDialogs.clearDialogProps} />
            {showQuickMarketSelector && <Tour tour={TourName.v257MarketCardUpdate} />}
            {showMultilegQuickMarketSelectorTour && (
              <Tour tour={TourName.v120MarketDataCardMultilegQuickMarketSelector} />
            )}
            {view !== CardViewEnum.COMPACT && (
              <MarketDataCardSlides
                config={initializedConfig}
                security={resolvedSecurity}
                activeOrder={clickToTradePreviewOrder}
                onClickPrice={handleClickPrice}
                onCancelActive={handleCancelActive}
                onSaveCard={onSave}
              />
            )}
            {showQuickMarketSelector && marketSelectorInventoryActive ? (
              <MarketDataCardQuickMarketSelectorPanel
                security={resolvedSecurity}
                ulEnabled={ulEnabled}
                selectedMarkets={selectedMarkets}
                selectedMarketAccounts={selectedMarketAccounts}
                unifiedLiquidityMarketSymbols={initializedConfig.unifiedLiquidityMarketSymbols ?? []}
                selectedAggregation={selectedAggregation}
                onPersistMarketSelection={onPersistMarketSelection}
                isOpen={quickPanelOpen}
                onClose={handleCloseQuickPanel}
                panelAnchorRef={cardRef}
                isNarrow={isPopout || isInMobileMode}
              />
            ) : null}
            <MaxOrderSizeDialogs {...maxOrderSizeDialogs} />
          </>
        )}
      </SharedMarketDataCard>
    ),
    [
      clickToTradePreviewOrder,
      defaultSubAccountId,
      detailsExpanded,
      handleCancelActive,
      handleClickPrice,
      handleCloseQuickPanel,
      handleDeleteCard,
      handleOpenMarketSelector,
      handleToggleSettings,
      initializedConfig,
      isDraggable,
      isDragged,
      isPopout,
      isInMobileMode,
      marketSelectorInventoryActive,
      maxOrderSizeDialogs,
      onDelete,
      onHidePlaceholders,
      onPersistMarketSelection,
      onSave,
      customRateDialogs,
      quickPanelOpen,
      resolvedSecurity,
      selectedAggregation,
      selectedMarketAccounts,
      selectedMarkets,
      showDelete,
      showMultilegQuickMarketSelectorTour,
      showQuickMarketSelector,
      showSettings,
      ulEnabled,
      view,
    ]
  );

  return (
    <Wrapper {...props} ref={cardRef}>
      {showQuickMarketSelector ? (
        <MarketDataCardQuickMarketSelectorProvider
          isOpen={quickPanelOpen}
          selectedMarkets={selectedMarkets}
          selectedMarketAccounts={selectedMarketAccounts}
          selectedAggregation={selectedAggregation}
          marketSelectorItems={marketSelectorItems}
          onPersistMarketSelection={onPersistMarketSelection}
        >
          {cardContent}
        </MarketDataCardQuickMarketSelectorProvider>
      ) : (
        cardContent
      )}
    </Wrapper>
  );
});

const Wrapper = styled(Box)`
  min-width: ${COLUMN_WIDTH}px;

  ${hideNavigableControlArrowsUntilHover}

  @media (hover: hover) {
    /** Keep icon-only controls visually compact until interaction reveals their text/span content. */
    ${DeepDiveButtonElement}:not(:hover):not(:focus-within),
    ${MarketCardMarketAccountButtonElement}:not(:hover):not(:focus-within) {
      ${InternalButtonSpan} {
        display: none;
      }
    }
  }

  ${DeepDiveButtonElement},
  ${MarketCardMarketAccountButtonElement} {
    ${InternalButtonSpan} {
      /** Let the height of the icon determine the height of the button */
      height: 0px;
    }
  }
`;
